import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FaPhoneAlt } from 'react-icons/fa';
import { MdOutlineMailOutline } from 'react-icons/md';
import { ROUTE_KEYS, EXTERNAL_URLS } from '../constants';
import { formatters } from '../helpers/formatters';
import { LogosFooter } from './footer/LogosFooter';
import SocialNetworks from './footer/SocialNetworks';
import { useColors } from 'legacyfrontend/hooks/useColors';
import { useTemplateConfig } from 'legacyfrontend/hooks/useTemplateConfig';
import {
  selectFooter,
  selectTemplateData,
  selectPrincipalOffice,
  selectPaisId,
} from 'legacyfrontend/redux/selectors/application-selectors';

export default function Footer() {
  const { t } = useTranslation(['layout']);
  const footer = useSelector(selectFooter);
  const templateData = useSelector(selectTemplateData);
  const principalOffice = useSelector(selectPrincipalOffice);
  const paisId = useSelector(selectPaisId);

  const {
    labels,
    legal,
    formatters,
    consumerRightsText,
    claimsBookText,
    consumerRightsLaw,
    showGeneralDirection,
    templateConfig,
  } = useTemplateConfig();

  const { background, text } = useColors();

  const renderLabelWithText = (list) =>
    list
      .filter((item) => item.text && item.text !== '')
      .map((item) =>
        item.link ? (
          <a
            key={`${item.label}-${item.text}`}
            className='contact-line hover'
            href={`${item.preffix}${item.text}`}
          >
            {item.icon && <i className={`${item.icon} pr-2`} />}
            <span className='no-underline hover:underline'>{item.text}</span>
          </a>
        ) : (
          <div className='contact-line' key={`${item.label}-${item.text}`}>
            {item.icon && <i className={`${item.icon} pr-2`} />}
            {item.text}
          </div>
        )
      );

  const infoList = useMemo(() => {
    if (!principalOffice) return [];

    return [
      {
        text: principalOffice.direccion,
        icon: 'fas fa-map-marker-alt',
      },
      {
        text: principalOffice.horario,
        icon: 'fas fa-clock',
      },
    ].filter((item) => item.text); // Solo elementos con texto
  }, [principalOffice]);

  const getUseTermsText = useMemo(() => {
    const termsLink = footer?.utilLink?.find((link) => {
      const linkName = link.tipoEnlace?.nombre?.toLowerCase() || '';
      return linkName.includes('términos') || linkName.includes('termos');
    });

    return termsLink?.tipoEnlace?.nombre || t('footer.useTerms');
  }, [footer?.utilLink, t]);

  const contactInfo = useMemo(() => {
    return renderLabelWithText(infoList);
  }, [infoList]);

  const legalInfo = useMemo(() => {
    const legalItems = [];

    if (legal.showCADASTUR && footer?.legajo) {
      legalItems.push(
        <span key='cadastur'>
          {labels.registration} {footer.legajo}
        </span>
      );
    }

    if (legal.showLegajo && footer?.legajo) {
      legalItems.push(
        <span key='legajo'>
          {labels.registration} {footer.legajo}
        </span>
      );
    }

    if (legal.showRUC && footer?.cuit) {
      legalItems.push(
        <span key='ruc'>
          {labels.taxId} {footer.cuit}
        </span>
      );
    }

    if (legal.showCUIT && footer?.cuit) {
      legalItems.push(
        <span key='cuit'>
          {labels.taxId} {footer.cuit}
        </span>
      );
    }

    if (legal.showCNPJ && footer?.cuit) {
      legalItems.push(
        <span key='cnpj'>
          {labels.taxId} {footer.cuit}
        </span>
      );
    }

    return legalItems;
  }, [legal, footer?.legajo, footer?.cuit, labels]);

  const mobileContactInfo = useMemo(
    () => (
      <div className='lg:hidden mt-4'>
        {templateData.header.telefonoContacto && (
          <div className='flex items-center gap-1 mb-1'>
            <FaPhoneAlt />
            <span className='contact__icon'>
              {formatters.phone(templateData.header.telefonoContacto)}
            </span>
          </div>
        )}
        {templateData.header.emailContacto && (
          <div className='flex items-center gap-1'>
            <MdOutlineMailOutline style={{ fontSize: '20px' }} />
            <span className='contact__icon lowercase'>{templateData.header.emailContacto}</span>
          </div>
        )}
      </div>
    ),
    [templateData.header, formatters]
  );

  const aboutUsText = useMemo(() => {
    return (
      footer.detalle?.find((detalle) => detalle.tipoEnlace?.nombre === 'about-us-footer')?.valor ||
      ''
    );
  }, [footer.detalle]);

  if (!footer || templateConfig?.hideFooter) {
    return null;
  }

  return (
    <footer className='footer-content'>
      <div className='py-8' style={{ backgroundColor: background, color: text }}>
        <div className='container'>
          <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-2 py-8 justify-items-stretch lg:divide-x-2 lg:divide-solid'>
            <div className='flex flex-col items-center md:items-start'>
              <div className='w-3/4'>
                <h3 className='text-2xl font-medium mb-8'>{t('footer.about_us')}</h3>
                <div className='text-sm text-justify mb-8'>
                  {aboutUsText}
                  {mobileContactInfo}
                </div>
              </div>
            </div>
            <div
              className='contact-section'
              style={{
                borderLeftColor: text,
                borderRightColor: text,
              }}
            >
              <div className='flex flex-col items-center'>
                <div className='w-3/4'>
                  <div className='text-2xl font-medium mb-8'>{t('footer.whereWeAre')}</div>
                  <div className='contact mb-8'>
                    <div className='flex flex-col items-start'>
                      <span>{footer.razonSocial.toUpperCase()}</span>

                      {/* Renderizar información legal específica por país */}
                      {legalInfo.map((item, index) => (
                        <div key={index} className='text-sm'>
                          {item}
                        </div>
                      ))}

                      <div className='flex flex-col items-start'>
                        {contactInfo}
                        <a
                          className='contact-line no-underline hover:underline'
                          href={ROUTE_KEYS.AGENCIES}
                        >
                          {t('footer.agencies')}
                        </a>
                      </div>
                    </div>

                    <SocialNetworks socialNetworks={templateData.socialNetworks} className='mt-4' />
                  </div>
                </div>
              </div>
            </div>

            <div
              className='info-section'
              style={{
                borderLeftColor: text,
                borderRightColor: text,
              }}
            >
              <div className='flex flex-col items-center'>
                <div className='w-3/4'>
                  <div className='text-2xl font-medium mb-8'>{t('footer.info')}</div>
                  <div className='content flex flex-col gap-1 items-start'>
                    {templateConfig.showClaimsBook && (
                      <a className='no-underline hover:underline' href={ROUTE_KEYS.CLAIMS}>
                        {claimsBookText}
                      </a>
                    )}
                    {templateConfig.showPaymentMethods && (
                      <a
                        className='no-underline hover:underline'
                        href={ROUTE_KEYS.PAY_METHODS}
                        target='_blank'
                      >
                        {t('footer.pay_methods')}
                      </a>
                    )}
                    {(templateConfig.showGeneralContractingConditions ||
                      templateConfig.showPrivacyPolicy) && (
                      <a
                        className='no-underline hover:underline'
                        href={ROUTE_KEYS.GENERAL_CONDITIONS}
                        target='_blank'
                      >
                        {paisId === 2
                          ? t('footer.generalConditionsPeru')
                          : t('footer.generalConditions')}
                      </a>
                    )}
                    {templateConfig.showTermsOfUse && (
                      <a
                        className='no-underline hover:underline'
                        href={ROUTE_KEYS.TERMS_AND_CONDITIONS}
                        target='_blank'
                      >
                        {getUseTermsText}
                      </a>
                    )}
                    {footer.utilLink &&
                      footer.utilLink.map((item) => (
                        <a
                          className='no-underline hover:underline'
                          href={item.valor}
                          key={item.id}
                          rel='noreferrer'
                          target='_blank'
                        >
                          {item.tipoEnlace?.descripcion}
                        </a>
                      ))}
                    {footer.legalLinks &&
                      footer.legalLinks.map((item) => (
                        <a
                          className='no-underline hover:underline'
                          href={item.valor}
                          target='_blank'
                          rel='noreferrer'
                          key={item.id}
                        >
                          {item.tipoEnlace?.descripcion}
                        </a>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {templateConfig.showConsumerRights && (
            <div className='terms-and-conditions-section text-sm text-justify'>
              {`${t('footer.consumerRights')} - ${t('footer.legalText')}`}
            </div>
          )}
          <div className='my-6 flex md:justify-between flex-col xl:flex-row'>
            {templateConfig.showConsumerRights && (
              <div className='flex justify-between flex-col lg:flex-row'>
                <div className='flex flex-col'>
                  <a
                    className='block w-72 h-12 p-4 mb-4 bg-white text-sm text-black uppercase text-center rounded-md'
                    href={t('footer.link-reclamations')}
                    rel='noreferrer'
                    target='_blank'
                  >
                    {consumerRightsText}
                  </a>
                  {consumerRightsLaw && <span>{consumerRightsLaw}</span>}
                  {showGeneralDirection && (
                    <>
                      <span>
                        {t('footer.nationalAuthority', 'Autoridad Nacional de aplicación')}
                      </span>
                      <span>
                        {t(
                          'footer.consumerDefenseText',
                          'Defensa de las y los Consumidores. Para reclamos ingrese'
                        )}
                        <a
                          className='font-bold hover:underline'
                          href={EXTERNAL_URLS.ARGENTINA_CONSUMER_DEFENSE}
                          rel='noreferrer'
                          target='_blank'
                        >
                          {' '}
                          {t('footer.here', 'aquí')}.
                        </a>
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
            <LogosFooter />
          </div>
        </div>
      </div>
    </footer>
  );
}
